// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createQueryString } from "@adl/XDL/Utilities";
import { ApiParameter, del, get, patch, post, request, ServiceResult } from "@insite/client-framework/Services/ApiService";
import {
    PaginationModel,
    ProductInventoryDto,
    ProductModel,
    ProductPriceDto,
} from "@insite/client-framework/Types/ApiModels";

const customQuotesUrl = "/api/custom/customquotes";

export interface CreateNewQuoteParams extends ApiParameter {
    name: string;
}

export interface AddQuoteToCartParams extends ApiParameter {
    quoteId: string;
}

export interface CustomQuoteLinesModel {
    lineId: string;
    productResult: ProductModel;
    unitOfMeasureDisplay: string;
    productErpNumber: string;
    qty: number;
    notes: string;
    extendedPrice: number;
    customerPrice: number;
    sortOrder: number;
    quotePrice?: number;
}

export interface CustomQuoteProductInfo {
    lineId?: string;
    qtyOrdered?: number;
    productId?: string;
    unitOfMeasure?: string;
    pricing?: ProductPriceDto;
    inventory?: ProductInventoryDto;
    failedToLoadPricing?: boolean;
    failedToLoadInventory?: boolean;
    productDetailPath?: string;
    quotePrice?: number;
}

export interface CustomQuoteModel {
    name: string;
    id: string;
    customerNumber: string;
    logoFileName: string;
    logoTooltipText: string;
    expiryDate: string;
    website: string;
    createdOn: string;
    modifiedOn: string;
    modifiedBy: string;
    linesCount: number;
    clientName: string;
    city: string;
    streetAddress1: string;
    streetAddress2: string;
    province: string;
    postalCode: string;
    email: string;
    phoneNumber: string;
    freightCharges: number;
    labourCharges: number;
    shopSupplies: number;
    priceDisplayType: string;
    priceDisplayTypes: string[];
    linesModel: CustomQuoteLinesModel[];
    allowRedirectToAssociatedQuote: boolean;
    isCameFromNormalQuote: boolean;
    quoteIdReference: string;
    generalNote: string;
}

export interface GetQuotesCollectionParams extends ApiParameter {
    search?: string;
    sort?: string;
    page?: number;
    pageSize?: number;
}

export interface DeleteCustomQuoteParams extends ApiParameter {
    quoteId: string;
}

export interface QuotesCollectionModel {
    customQuoteCollection: CustomQuoteModel[];
    pagination: PaginationModel;
}

export interface CustomQuoteResponse {
    quotes: QuotesCollectionModel;
}

export interface ConvertListToQuoteParams extends ApiParameter {
    wishlistId: string;
    selectedWishListLineIds?: string[];
}

export interface LoadQuoteParams extends ApiParameter {
    quoteId: string;
    sortBy?: string;
    skipPricingAndInventory?: boolean;
}

export interface AddItemToQuoteParams extends ApiParameter {
    customQuoteId: string;
    linesModel: {
        qty: number;
        productErpNumber: string;
        sortOrder: number;
    }[];
}

export interface DeleteQuoteLineParams extends ApiParameter {
    lineId?: string;
}

export interface RenameCustomQuoteParams extends ApiParameter {
    customQuoteId: string;
    newName: string;
}

export interface UpdateCustomQuoteLinesParams extends ApiParameter {
    linesModel: any[];
    updateQty?: boolean;
    updatePrice?: boolean;
    updateNote?: boolean;
    updateSort?: boolean;
}

export interface GeneratePdfParams extends ApiParameter {
    id?: string;
    sendPdfByEmail?: boolean; // in case the user wants to send the pdf via email
    streetAddress1?: string;
    streetAddress2?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    website?: string;
    email?: string;
    clientName?: string;
    phoneNumber?: string;
    freightCharges?: number;
    labourCharges?: number;
    priceDisplayOption?: string;
    expirationDate?: string;
    pdfDestinationEmailAddress?: string;
}

export interface UploadLogoParams {
    formData: any;
    customQuoteId: string;
}

export interface convertToCustomQuoteParams {
   quoteId: string;
}

export function createCustomQuote(params: CreateNewQuoteParams) {
    return post<any>(`${customQuotesUrl}/CreateNew`, { name: params.name });
}

export function getCustomQuotes(params: GetQuotesCollectionParams) {
    const timestamp = `time${new Date().getTime()}`;
    const query = createQueryString([
        `page=${params.page || 1}`,
        `${params.pageSize ? `pageSize=${params.pageSize}` : ""}`,
        `${params.search ? `search=${params.search}` : ""}`,
        `${params.sort ? `sort=${params.sort}` : ""}`,
        timestamp,
    ]);
    return get<CustomQuoteResponse>(`${customQuotesUrl}${query}`);
}

export function deleteCustomQuote(params: DeleteCustomQuoteParams) {
    return del(`${customQuotesUrl}/${params.quoteId}`);
}

export function addQuoteToCart(params: AddQuoteToCartParams) {
    return post<any>(`${customQuotesUrl}/AddToCart/${params.quoteId}`);
}

export function loadCustomQuote(params: LoadQuoteParams) {
    const query = createQueryString([params.sortBy ? `sortBy=${params.sortBy}` : ""]);
    return get<CustomQuoteModel>(`${customQuotesUrl}/${params.quoteId}${query}`);
}

export function convertListToQuoteApi(params: ConvertListToQuoteParams) {
    return post<any>(`${customQuotesUrl}/ConvertList/${params.wishlistId}`, params.selectedWishListLineIds);
}

export function addItemToQuoteApi(params: AddItemToQuoteParams) {
    return post<any>(`${customQuotesUrl}/AddNewItem`, params);
}

export function renameCustomQuote(params: RenameCustomQuoteParams) {
    return patch<any>(`${customQuotesUrl}/Rename`, params);
}

export function deleteQuoteLine(params: DeleteQuoteLineParams) {
    return del(`${customQuotesUrl}/DeleteItem/${params.lineId}`);
}

export function updateQuoteLine(params: UpdateCustomQuoteLinesParams) {
    return patch<any>(`${customQuotesUrl}/UpdateLineInfo`, params);
}

export function getAvailableNames() {
    return get<any>(`${customQuotesUrl}/getAvailableNames`);
}

export function generatePdf(params: GeneratePdfParams) {
    return post<any>(`${customQuotesUrl}/GeneratePdf`, params);
}

export function uploadLogoApi(params: UploadLogoParams): Promise<ServiceResult<any>> {
    return request<any>(
        `${customQuotesUrl}/UploadLogo/${params.customQuoteId}`,
        "POST",
        {},
        params ? (params.formData as any) : undefined,
    );
}

export function convertToCustomQuoteApi(quoteId: string) {
    return post<any>(`${customQuotesUrl}/${quoteId}/ConvertToCustomQuote`);
}

export function savePdfDate(params: GeneratePdfParams) {
    return post<any>(`${customQuotesUrl}/SavePdfData`, params);
}